import { ApiBaseService } from './ApiBaseService';

export interface CompanyOption {
  id: string;
  name: string;
}

export interface SpaceWithToggle {
  id: string;
  name: string;
  loggingEnabled: boolean;
}

export interface CompanyWithSpaces {
  id: string;
  name: string;
  spaces: SpaceWithToggle[];
}

export interface RunLogListItem {
  id: string;
  rootFlowId: string;
  rootFlowName: string;
  runType: string;
  status: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  totalTokens: number;
  totalCost: number;
  errorMessage?: string;
}

export interface RunLogListResponse {
  data: RunLogListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface RunLogNode {
  nodeId: string;
  nodeType: string;
  label?: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  status: 'success' | 'error';
  errorMessage?: string;
  tokens?: number;
  cost?: number;
  settings?: unknown;
  input?: unknown;
  output?: unknown;
  rawResponse?: unknown;
  attempts?: number;
  modelId?: string;
  modelProvider?: string;
  innerSteps?: RunLogNode[];
  subRun?: RunLogDocument;
}

export type RunLogErrorKind = 'node' | 'subRun';

export interface RunLogErrorEntry {
  kind: RunLogErrorKind;
  flowPath: string[];
  stepPath: string;
  nodeId: string;
  nodeType: string;
  nodeLabel: string;
  errorMessage: string;
}

export interface RunLogSummary {
  errorCount: number;
  errors: RunLogErrorEntry[];
}

export interface RunLogDocument {
  processId: string;
  rootFlowId: string;
  rootFlowName: string;
  spaceId: string;
  spaceName?: string;
  orgId?: string;
  orgName?: string;
  backendVersion?: string;
  runType: string;
  triggeredBy?: { userId?: string; source?: string };
  startedAt: string;
  endedAt: string;
  durationMs: number;
  totalTokens: number;
  totalCost: number;
  status: 'success' | 'error';
  errorMessage?: string;
  input?: unknown;
  output?: unknown;
  nodes: RunLogNode[];
  summary?: RunLogSummary;
}

export interface RunLogDetailResponse {
  id: string;
  spaceId: string;
  rootFlowId: string;
  rootFlowName: string;
  runType: string;
  status: string;
  errorMessage?: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  totalTokens: number;
  totalCost: number;
  logData: RunLogDocument;
}

interface ApiWrapper<T> {
  success: boolean;
  data: T;
}

const BASE = '/internal/admin/space-logging';

class AdminSpaceLoggingService {
  static async listCompanies(): Promise<CompanyOption[]> {
    const res = await ApiBaseService.get<ApiWrapper<CompanyOption[]>>(
      `${BASE}/companies`,
    );
    return res.data;
  }

  static async listSpacesForCompany(
    companyId: string,
  ): Promise<CompanyWithSpaces> {
    const res = await ApiBaseService.get<ApiWrapper<CompanyWithSpaces>>(
      `${BASE}/companies/${companyId}/spaces`,
    );
    return res.data;
  }

  static async toggleLogging(
    spaceId: string,
    enabled: boolean,
  ): Promise<{ id: string; loggingEnabled: boolean }> {
    const res = await ApiBaseService.post<
      ApiWrapper<{ id: string; loggingEnabled: boolean }>
    >(`${BASE}/spaces/${spaceId}/toggle`, { enabled }, {});
    return res.data;
  }

  static async listRuns(
    spaceId: string,
    page: number,
    limit: number,
  ): Promise<RunLogListResponse> {
    const res = await ApiBaseService.get<ApiWrapper<RunLogListResponse>>(
      `${BASE}/runs?spaceId=${encodeURIComponent(
        spaceId,
      )}&page=${page}&limit=${limit}`,
    );
    return res.data;
  }

  static async getRun(processId: string): Promise<RunLogDetailResponse> {
    const res = await ApiBaseService.get<ApiWrapper<RunLogDetailResponse>>(
      `${BASE}/runs/${processId}`,
    );
    return res.data;
  }
}

export default AdminSpaceLoggingService;
